import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import NotificationDropdown from "./NotificationDropdown";

const formatUnreadCount = (count) => {
    const unreadCount = Number(count);

    if (!Number.isFinite(unreadCount) || unreadCount <= 0) return "";
    if (unreadCount > 50) return "50+";

    return String(Math.trunc(unreadCount));
};

const NotificationBell = ({
    className = "",
    buttonClassName = "",
    dropdownClassName = "",
    open,
    defaultOpen = false,
    unreadCount: unreadCountProp,
    onOpenChange,
    onNotificationClick,
}) => {
    const navigate = useNavigate();
    const storeUnreadCount = useSelector(
        (state) => state.notifications?.unreadCount || 0,
    );
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const containerRef = useRef(null);
    const isControlled = typeof open === "boolean";
    const isOpen = isControlled ? open : internalOpen;
    const unreadCount =
        unreadCountProp === undefined ? storeUnreadCount : unreadCountProp;
    const unreadLabel = formatUnreadCount(unreadCount);
    const hasUnread = Boolean(unreadLabel);

    const setOpen = useCallback(
        (nextOpen) => {
            if (!isControlled) {
                setInternalOpen(nextOpen);
            }

            onOpenChange?.(nextOpen);
        },
        [isControlled, onOpenChange],
    );

    useEffect(() => {
        if (!isOpen) return undefined;

        const handlePointerDown = (event) => {
            if (containerRef.current?.contains(event.target)) return;

            setOpen(false);
        };

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setOpen(false);
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, setOpen]);

    const handleNotificationClick = (notification, actionContext) => {
        if (onNotificationClick) {
            onNotificationClick(notification, actionContext);
            return;
        }

        const navigationTarget =
            actionContext?.route || actionContext?.resultsUrl || null;

        if (!navigationTarget) return;

        setOpen(false);
        navigate(navigationTarget);
    };

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            <button
                type="button"
                onClick={() => setOpen(!isOpen)}
                aria-label={
                    hasUnread
                        ? `Open notifications, ${unreadLabel} unread`
                        : "Open notifications"
                }
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                className={`relative inline-flex h-8 w-8 items-center justify-center rounded-full border border-indigo-100 bg-indigo-50 text-slate-600 shadow-sm transition hover:border-indigo-100 hover:bg-indigo-100 hover:text-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-100 ${buttonClassName}`}
            >
                <Bell size={17} aria-hidden="true" />

                {hasUnread && (
                    <span className="absolute -right-1 -top-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full border-2 border-white bg-indigo-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white shadow-sm">
                        {unreadLabel}
                    </span>
                )}
            </button>

            {isOpen && (
                <NotificationDropdown
                    className={dropdownClassName}
                    onNotificationClick={handleNotificationClick}
                />
            )}
        </div>
    );
};

export default NotificationBell;
